import { ChangeDetectionStrategy, Component, ElementRef, NgZone, afterRenderEffect, computed, effect, inject, signal, untracked, viewChild } from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faLink } from '@fortawesome/free-solid-svg-icons';
import {
  faFacebook,
  faGithub,
  faInstagram,
  faLinkedin,
  faPinterest,
  faSnapchat,
  faTiktok,
  faTwitter,
  faWhatsapp,
  faYoutube,
} from '@fortawesome/free-brands-svg-icons';
import { ValueFirstOnboardingService } from '../../services/value-first-onboarding.service';
import { CampaignCurrency, VALUE_FIRST_CURRENCIES, brandInitial, detectCampaignCurrency } from '../../services/value-first-onboarding.types';

const SOCIAL_ICONS: Record<string, IconDefinition> = {
  facebook: faFacebook,
  github: faGithub,
  instagram: faInstagram,
  linkedin: faLinkedin,
  pinterest: faPinterest,
  snapchat: faSnapchat,
  tiktok: faTiktok,
  twitter: faTwitter,
  x: faTwitter,
  whatsapp: faWhatsapp,
  youtube: faYoutube,
};

@Component({
  selector: 'vf-found-step',
  imports: [FaIconComponent],
  templateUrl: './found-step.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class VfFoundStep {
  readonly onboarding = inject(ValueFirstOnboardingService);
  readonly currencies = VALUE_FIRST_CURRENCIES;
  readonly descriptionOpen = signal(false);
  readonly descriptionOverflows = signal(false);

  private readonly zone = inject(NgZone);
  private readonly descriptionText = computed(() => this.onboarding.draft().brand?.description ?? '');
  private readonly descriptionRef = viewChild<ElementRef<HTMLParagraphElement>>('description');

  constructor() {
    effect(() => {
      this.descriptionText();
      untracked(() => this.descriptionOpen.set(false));
    });

    afterRenderEffect((onCleanup) => {
      const open = this.descriptionOpen();
      this.descriptionText();
      const element = this.descriptionRef()?.nativeElement;
      if (!element || typeof ResizeObserver === 'undefined') {
        return;
      }
      const measure = (): void => {
        if (untracked(this.descriptionOpen)) {
          return;
        }
        const overflows = element.scrollHeight > element.clientHeight + 1;
        this.zone.run(() => this.descriptionOverflows.set(overflows));
      };
      if (!open) {
        measure();
      }
      const observer = new ResizeObserver(measure);
      observer.observe(element);
      onCleanup(() => observer.disconnect());
    });
  }

  descriptionClass(): string {
    return this.descriptionOpen() ? '' : 'line-clamp-3 lg:line-clamp-4';
  }

  toggleDescription(): void {
    this.descriptionOpen.update((open) => !open);
  }

  socialIcon(type: string): IconDefinition {
    const key = type.toLowerCase().replace(/[^a-z0-9]/g, '');
    return SOCIAL_ICONS[key] ?? faLink;
  }

  initial(): string {
    return brandInitial(this.onboarding.brandName());
  }

  chooseCurrency(currency: CampaignCurrency): void {
    this.onboarding.setCurrency(currency);
  }

  currencyDetected(): boolean {
    const brand = this.onboarding.draft().brand;
    if (!brand) {
      return false;
    }
    const detection = detectCampaignCurrency(brand.domain, brand.countryCode);
    return detection.detected && detection.currency === this.onboarding.activeCurrency();
  }

  edit(): void {
    this.onboarding.editBrand();
  }

  reject(): void {
    this.onboarding.rejectBrand();
  }

  claim(): void {
    this.onboarding.claimWorkspace();
  }
}
